import { create } from 'zustand';

export const useNotificationsStore = create((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  offset: 0,
  limit: 10,
  hasMore: true,

  // Fetch notifications
  fetchNotifications: async (accessToken, reset = false) => {
    const state = get();

    if (reset) {
      set({ notifications: [], offset: 0, hasMore: true });
    }

    if (!state.hasMore && !reset) return;

    set({ isLoading: true });

    try {
      const currentOffset = reset ? 0 : state.offset;
      const response = await fetch(`/api/notifications?limit=${state.limit}&offset=${currentOffset}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        const newNotifications = reset ? data.notifications : [...state.notifications, ...data.notifications];
        set({
          notifications: newNotifications,
          unreadCount: data.unread_count,
          hasMore: data.notifications.length === state.limit,
          offset: currentOffset + data.notifications.length,
          isLoading: false
        });
      } else {
        console.error('Failed to fetch notifications:', data.message);
        set({ isLoading: false });
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      set({ isLoading: false });
    }
  },

  // Fetch unread count only
  fetchUnreadCount: async (accessToken) => {
    try {
      const response = await fetch('/api/notifications/count', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        set({ unreadCount: data.unread_count });
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  },

  // Mark notification as viewed
  markAsViewed: async (accessToken, notificationId) => {
    try {
      const response = await fetch(`/api/notifications/${notificationId}/view`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        // Update notification in local state
        const state = get();
        const updatedNotifications = state.notifications.map(notif => {
          if (notif.id === notificationId) {
            return { ...notif, viewed_at: new Date().toISOString() };
          }
          return notif;
        });

        // Re-sort: unread first, then by created_at
        const sortedNotifications = updatedNotifications.sort((a, b) => {
          if (!a.viewed_at && b.viewed_at) return -1;
          if (a.viewed_at && !b.viewed_at) return 1;
          return new Date(b.created_at) - new Date(a.created_at);
        });

        // Decrement unread count if it was unread
        const wasUnread = state.notifications.find(n => n.id === notificationId && !n.viewed_at);

        set({
          notifications: sortedNotifications,
          unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount
        });

        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error marking notification as viewed:', error);
      return { success: false, message: error.message };
    }
  },

  // Reset store
  reset: () => {
    set({
      notifications: [],
      unreadCount: 0,
      offset: 0,
      hasMore: true
    });
  }
}));
